import { useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

import { usePrevious } from 'utils/reactHooks';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { TabPanel } from 'components/shared';
import StyledTabs from 'components/shared/StyledTabs';

import Contacts from './Contacts/Contacts';
import DeployHistory from './DeployHistory/DeployHistory';
import RegionalPool from './RegionalPool/RegionalPool';
import AIMatchSettings from './AIMatch/AIMatch';
import { Box } from '@mui/material';
import useSubNavigation from 'hooks/useSubNavigation';
import { StyledStickyPaper } from 'components/shared/Paper/StyledStickyPaper';
import KidsPlan from './KidsPlan/KidsPlan';

enum DOJO_TAB {
  ADMIN = 'ADMIN',
  IMPORT = 'IMPORT',
  ROSTER = 'ROSTER'
}

export default function Settings() {
  const { t } = useTranslation('common');
  const dojoTabRefs = useRef<{ [key in DOJO_TAB]: HTMLDivElement }>({
    [DOJO_TAB.ADMIN]: document.createElement('div'),
    [DOJO_TAB.IMPORT]: document.createElement('div'),
    [DOJO_TAB.ROSTER]: document.createElement('div')
  });
  const { activePage: _activePage, setPage } = useSubNavigation<
    DOJO_TAB | string
  >(DOJO_TAB.ADMIN.toLowerCase());
  const {
    hasRegionalPoolOrganisationAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasDutyAutoAskEmployeesBasic,
    hasKidsPlan
  } = useSelector((state) => state.grants);

  const activePage = _activePage.toUpperCase();

  const onTabChanged = (_, newValue: DOJO_TAB | string) => {
    setPage(newValue.toLowerCase());
  };

  const onDojoTabRender = (dojoTab: DOJO_TAB, key: string) => {
    const container = document.createElement('div');
    dojoTabRefs.current[dojoTab]!.appendChild(container);
    window.dojoTopic.publish(key, container);
  };

  const onDojoTabDestroy = (key: string) => window.dojoTopic.publish(key);

  const tabs = [
    {
      id: DOJO_TAB.ADMIN,
      renderId: 'renderAdminSettingsAdminTab',
      destroyId: 'destroyAdminSettingsAdminTab',
      title: t('admin'),
      grant: true
    },
    {
      id: DOJO_TAB.ROSTER,
      renderId: 'renderAdminSettingsRosterTab',
      destroyId: 'destroyAdminSettingsRosterTab',
      title: t('roster'),
      grant: true
    },
    {
      id: DOJO_TAB.IMPORT,
      renderId: 'renderAdminSettingsImportTab',
      destroyId: 'destroyAdminSettingsImportTab',
      title: t('import'),
      grant: true
    },
    {
      id: 'KIDS_PLAN',
      title: t('adminPortal.settings.kidsPlan.tabTitle'),
      content: <KidsPlan />,
      grant: hasKidsPlan
    },
    {
      id: 'REGIONAL_POOL',
      title: t('regionalPool'),
      content: <RegionalPool />,
      grant: hasRegionalPoolOrganisationAccess
    },
    {
      id: 'CONTACTS',
      title: t('contacts'),
      content: <Contacts />,
      grant: true
    },
    {
      id: 'DEPLOY_HISTORY',
      title: t('deployHistory.title'),
      content: <DeployHistory />,
      grant: true
    },
    {
      id: 'AI_MATCH_SETTINGS',
      title: t('aiPlanning'),
      content: <AIMatchSettings />,
      grant: hasAutoAskDutyRoundsViewAccess || hasDutyAutoAskEmployeesBasic
    }
  ];

  const previousPage: DOJO_TAB | string | undefined = usePrevious(activePage);

  useEffect(() => {
    if (!previousPage) return;

    const destroyDojoWidget = onDojoTabDestroy.bind(
      null,
      tabs?.[previousPage]?.destroyId
    );

    if (previousPage in DOJO_TAB) destroyDojoWidget();
    return () => destroyDojoWidget();
  }, [activePage]);

  return (
    <StyledTabs className="tabs">
      <StyledStickyPaper square>
        <Tabs
          value={activePage}
          indicatorColor="primary"
          textColor="primary"
          onChange={onTabChanged}
        >
          {tabs
            .filter((tab) => tab.grant)
            .map((tab, index) => (
              <Tab key={index} label={tab.title} value={tab.id} />
            ))}
        </Tabs>
      </StyledStickyPaper>

      <Box className="content">
        {tabs
          .filter((tab) => tab.grant)
          .map((tab, i: number) => {
            const isDojoTab = !!tab.id && !!tab.renderId;

            // Memoize onRender
            const onRender = useCallback(() => {
              onDojoTabRender(tab.id as DOJO_TAB, tab.renderId ?? '');
            }, [tab.id, tab.renderId]);

            return (
              <TabPanel
                key={i}
                value={tab.id}
                activePage={activePage}
                title={tab.title}
                {...(isDojoTab
                  ? {
                      onRender: onRender
                    }
                  : {})}
              >
                {isDojoTab ? (
                  <div
                    ref={(ref) => {
                      if (ref) {
                        dojoTabRefs.current[tab.id] = ref;
                      }
                    }}
                    style={{ height: '100%' }}
                  />
                ) : (
                  tab.content
                )}
              </TabPanel>
            );
          })}
      </Box>
    </StyledTabs>
  );
}
